/* G3D custom (2026-10-09): Starter Trio Bundle page (sections/starter-trio.liquid), direction C "card fan". Ported from
   the approved mockup (Obsidian vault: Website/mockups/starter-trio-c.html, after the phase-2 debloat). Variants, prices,
   stock and photos come from the #st-data block the section renders. Add to cart fires the theme's CartLinesUpdateEvent,
   like sections/build-your-bundle.liquid, so the header bubble and the cart drawer refresh. */
import { addToCart } from '@theme/g3d-cart';
import { lockScroll, unlockScroll } from '@theme/utilities';

const root = document.querySelector('.g3d-st');
const $ = id => document.getElementById(id);
const DATA = JSON.parse($('st-data').textContent);
const TYPES = ['Grass', 'Fire', 'Water'];
const size = (url, w) => url.replace(/width=\d+/, 'width=' + w); // the section hands out 1000px image URLs
const photo = alt => DATA.photos.find(p => p.alt.startsWith(alt))?.src;
// [region, then Grass/Fire/Water as [name, national dex no. (0 = not public yet)]]
const GENS = [
  ['Kanto', ['Bulbasaur', 1], ['Charmander', 4], ['Squirtle', 7]],
  ['Johto', ['Chikorita', 152], ['Cyndaquil', 155], ['Totodile', 158]],
  ['Hoenn', ['Treecko', 252], ['Torchic', 255], ['Mudkip', 258]],
  ['Sinnoh', ['Turtwig', 387], ['Chimchar', 390], ['Piplup', 393]],
  ['Unova', ['Snivy', 495], ['Tepig', 498], ['Oshawott', 501]],
  ['Kalos', ['Chespin', 650], ['Fennekin', 653], ['Froakie', 656]],
  ['Alola', ['Rowlet', 722], ['Litten', 725], ['Popplio', 728]],
  ['Galar', ['Grookey', 810], ['Scorbunny', 813], ['Sobble', 816]],
  ['Paldea', ['Sprigatito', 906], ['Fuecoco', 909], ['Quaxly', 912]],
  // the store tags Browt "winds-and-waves" (the Gen 10 games); the region name isn't used anywhere in the store yet
  ['Winds & Waves', ['Browt', 0], ['Pombon', 0], ['Gecqua', 0]]
].map(([region, ...s], i) => {
  const tag = 'Gen ' + (i + 1);
  // a starter's photo: its single ball's photo, else a bundle photo of it, else none ("photo soon")
  const starters = s.map(([name, dex], t) => ({ name, dex, type: TYPES[t], c: `var(--${TYPES[t].toLowerCase()})`,
    file: DATA.balls[name.toLowerCase() + '-ball'] || photo(`${tag} - ${name} (photographed)`) }));
  return { n: i + 1, region, label: `${tag} · ${region}`, trio: photo(`${tag} - Trio Photo`), starters,
    photos: starters.filter(x => x.file).length,
    variants: Object.fromEntries(['Normal', 'Shiny'].map(st => [st, DATA.variants.find(v => v.gen === tag && v.style === st)])) };
});
console.assert(GENS.every(g => g.variants.Normal && g.variants.Shiny), 'a generation has no Normal/Shiny variant in the store');

const dexNo = st => 'No.' + (st.dex ? String(st.dex).padStart(3, '0') : '???');
// a starter's ball photo, or the "photo soon" tile
const tile = st => st.file ? `<img src="${size(st.file, 500)}" alt="${st.name} Ball">`
  : `<span class="st-soon" style="--c:${st.c}" role="img" aria-label="${st.name} Ball, photo coming soon"><i></i><b>${st.name}</b><small>Photo soon</small></span>`;
const replay = (el, cls) => { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); };
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

const ICON = { // our own simple type marks: leaf, flame, drop
  Grass: 'M20 4C9 4 4 10 4 17c0 1 .1 2 .3 3C6 13 11 9 16 8c-5 3-8 7-9.5 12.5.8.3 1.7.5 2.5.5C17 21 20 13 20 4z',
  Fire: 'M12 2c1 4 6 6 6 12a6 6 0 0 1-12 0c0-3 2-5 3-6 0 2 1 3 2 3 0-4-1-6 1-9z',
  Water: 'M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13z'
};
const typeMark = t => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${ICON[t]}"/></svg>${t}`;
const stage = root.querySelector('.st-stage'), fan = $('st-fan'), strip = $('st-strip'), atc = $('st-atc');

// spans, not divs: everything inside a button has to be phrasing content
fan.innerHTML = TYPES.map((t, i) => `
  <button type="button" class="st-card" data-t="${i}" style="--i:${i};--c:var(--${t.toLowerCase()})">
    <span class="st-card__in">
      <span class="st-face st-front">
        <i class="st-foil"></i>
        <span class="st-card__top"><b></b><span></span></span>
        <span class="st-ph st-card__ph"></span>
        <span class="st-card__foot"><span class="st-type">${typeMark(t)}</span></span>
      </span>
      <span class="st-face st-back" aria-hidden="true"><b>G3D</b></span>
    </span>
  </button>`).join('');
const cards = [...fan.querySelectorAll('.st-card')];

strip.innerHTML = GENS.map((g, i) => `<button type="button" class="st-gen" role="radio" aria-label="${g.label}" data-gen="${i}">
  <span class="st-mini" aria-hidden="true">${g.starters.map(s => `<span style="--c:${s.c}">${s.file ? `<img src="${size(s.file, 80)}" alt="">` : ''}</span>`).join('')}</span>
  <b>Gen ${g.n}</b><small>${g.region}</small></button>`).join('') + '<span class="st-pball" aria-hidden="true"></span>';
const gens = [...strip.querySelectorAll('.st-gen')], marker = strip.querySelector('.st-pball');
$('st-gens').insertAdjacentHTML('beforeend', GENS.map((g, i) =>
  `<label class="st-opt"><input type="radio" name="st-gen" value="${i}"><span><b>${g.n}</b><small>${g.region}</small></span></label>`).join(''));

/* ---- state: the buy box, the cards and the strip all read it; any of them can change it ---- */
const current = DATA.variants.find(v => v.id === DATA.current) || DATA.variants[0];
const state = { gen: Math.max(0, GENS.findIndex(g => 'Gen ' + g.n === current.gen)), style: current.style === 'Shiny' ? 'Shiny' : 'Normal' };
const variant = () => GENS[state.gen].variants[state.style];
let addedTimer, busy = false, timers = [];

function render() {
  const g = GENS[state.gen], shiny = state.style === 'Shiny', on = gens[state.gen], v = variant();
  root.querySelectorAll('.st-opt input').forEach(inp => inp.checked = inp.value === String(inp.name === 'st-gen' ? state.gen : state.style));
  $('st-genName').textContent = '· ' + g.label;
  // the price sits in a live region: rewriting the same text would announce it again
  if ($('st-price').textContent !== v.money) $('st-price').textContent = v.money;
  if ($('st-save').textContent !== 'Save ' + v.save) $('st-save').textContent = $('st-tagSave').textContent = 'Save ' + v.save;
  $('st-save').hidden = $('st-tagSave').hidden = !v.save;
  $('st-boxIntro').textContent = `3 ${shiny ? 'shiny ' : ''}balls from ${g.label}, one per type:`;
  $('st-boxList').innerHTML = g.starters.map(s => `<li style="--c:${s.c}">${s.name}<small>${s.type}</small></li>`).join('');
  $('st-boxFig').hidden = !g.trio;
  if (g.trio) {
    $('st-boxImg').src = size(g.trio, 800);
    $('st-boxImg').alt = `The ${g.label} set: ${g.starters.map(s => s.name).join(', ')}`;
    $('st-boxCap').textContent = `Real photo of the ${g.label} set${shiny ? ' (normal colours)' : ''}`;
  }
  const todo = g.starters.filter(s => !s.file).map(s => s.name);
  $('st-photoNote').textContent = !todo.length ? '' : g.photos
    ? `${todo.join(' and ')} ${todo.length > 1 ? 'are' : 'is'} still waiting for a first print run, so ${todo.length > 1 ? 'they show' : 'it shows'} a "photo soon" tile. Orderable now either way.`
    : `This set hasn't had its first print run yet, so there are no photos. It's fully orderable now; photos follow the first print.`;
  $('st-photoNote').hidden = !todo.length;
  atc.disabled = !v.available;
  atc.textContent = !v.available ? 'Sold out' : busy ? 'Adding…' : atc.classList.contains('st-is-added') ? 'Added to cart ✓'
    : atc.classList.contains('st-is-error') ? "Couldn't add it. Try again" : 'Add to cart · ' + v.money;

  $('st-genTag').textContent = g.label;
  gens.forEach((b, i) => { b.setAttribute('aria-checked', i === state.gen); b.tabIndex = i === state.gen ? 0 : -1; });
  marker.style.transform = `translateX(${on.offsetLeft + on.offsetWidth / 2 - 9}px)`;
  stage.classList.toggle('st-is-shiny', shiny);
  $('st-note').innerHTML = shiny ? '<i class="st-star">✦</i> Shiny prints come in their shiny colours. The photos show the normal print for now.'
    // which balls still wait for photos is said once, in the box below
    : g.photos === 3 ? 'Open a card to see its photo up close, or deal another generation below.'
    : g.photos ? 'Open a card with a photo to see it up close, or deal another generation below.'
    : 'Deal another generation below.';
}

// keep the chosen generation in view inside the strip without scrolling the page; only on a real change, so it never pulls back a swipe
const centre = () => { const on = gens[state.gen]; strip.scrollTo({ left: on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2, behavior: REDUCED ? 'auto' : 'smooth' }); };

function fillCard(card, t) {
  const g = GENS[state.gen], st = g.starters[t];
  card.querySelector('.st-card__top b').textContent = st.name;
  card.querySelector('.st-card__top span').textContent = dexNo(st);
  card.querySelector('.st-ph').innerHTML = tile(st);
  // a "photo soon" card has nothing to zoom into
  card.disabled = !st.file;
  card.setAttribute('aria-label', `${st.name} Ball, ${st.type}, ${st.file ? 'view the photo up close' : 'photo coming soon'}`);
}

function set(patch) {
  patch = Object.fromEntries(Object.entries(patch).filter(([k, v]) => state[k] !== v));
  if (!Object.keys(patch).length) return;
  Object.assign(state, patch);
  clearTimeout(addedTimer);
  atc.classList.remove('st-is-added', 'st-is-error');
  render();
  // keep ?variant= in step, like the theme's own picker, so shared links open this generation and style
  const u = new URL(location.href);
  u.searchParams.set('variant', variant().id);
  history.replaceState(history.state, '', u);
  if (!('gen' in patch)) return;
  centre();
  // deal: flip all three; each front is refilled while its back faces the viewer
  timers.forEach(clearTimeout);
  if (REDUCED) return cards.forEach(fillCard);
  timers = cards.map((c, t) => { replay(c, 'st-is-flip'); return setTimeout(() => fillCard(c, t), 400 + t * 90); });
}
root.addEventListener('change', e => {
  if (e.target.name === 'st-gen') set({ gen: +e.target.value });
  if (e.target.name === 'st-style') set({ style: e.target.value });
});
strip.onclick = e => { const b = e.target.closest('.st-gen'); if (b) set({ gen: +b.dataset.gen }); };
strip.onkeydown = e => { // radiogroup arrow keys
  const d = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[e.key];
  if (!d) return;
  e.preventDefault();
  set({ gen: (state.gen + d + GENS.length) % GENS.length });
  gens[state.gen].focus();
};
// only a real width change re-deals: iOS Safari fires resize whenever its toolbar hides or shows while scrolling
let lastW = innerWidth;
addEventListener('resize', () => { if (innerWidth !== lastW) { lastW = innerWidth; render(); centre(); } });

/* ---- add to cart: the same event the theme's own add-to-cart fires, so the header bubble and the cart drawer refresh ---- */
function flash(cls, message) {
  busy = false;
  $('st-status').textContent = message;
  atc.classList.add(cls);
  render();
  clearTimeout(addedTimer);
  addedTimer = setTimeout(() => { atc.classList.remove(cls); render(); }, cls === 'st-is-added' ? 2000 : 2600);
}
atc.onclick = e => {
  e.preventDefault(); // the form posts only when this script never ran
  const v = variant(), g = GENS[state.gen], style = state.style;
  if (busy || !v.available) return;
  // the header announces the cart count from the same event, so this message leaves it out
  const msg = `Starter Trio Bundle, ${g.label}, ${style}, added to your cart.`;
  // "Added ✓" only on the choice that was added; the shopper may have picked another one meanwhile
  const added = () => { if (variant() === v) flash('st-is-added', msg); else { busy = false; $('st-status').textContent = msg; render(); } };
  busy = true;
  render();
  addToCart(atc, [{ id: v.id, quantity: 1 }], 'starter-trio') // a failed cart refresh still counts as added: a retry would add a second one
    .then(added, err => flash('st-is-error', "Couldn't add the bundle to your cart: " + err.message))
    .finally(() => { busy = false; });
};

/* ---- photo viewer: steps through this generation's photographed balls ---- */
const lb = $('st-lb'), box = lb.firstElementChild, lbImg = $('st-lbImg');
let list = [], at = 0, opener;
function show(i) {
  at = (i + list.length) % list.length;
  const st = list[at], shiny = state.style === 'Shiny', src = size(st.file, 1000);
  if (lbImg.src !== src) { lbImg.classList.add('st-is-loading'); lbImg.src = src; }
  // the photo is always the normal print, so shiny says so instead of pretending
  lbImg.alt = st.name + ' Ball' + (shiny ? ', normal colours shown' : '');
  $('st-lbName').textContent = st.name;
  $('st-lbNo').textContent = dexNo(st);
  $('st-lbType').innerHTML = typeMark(st.type);
  $('st-lbType').style.setProperty('--c', st.c);
  $('st-lbPos').textContent = list.length > 1 ? `${at + 1} / ${list.length}` : '';
  $('st-lbSub').textContent = GENS[state.gen].label + (shiny ? ' · Shiny print, shown here in normal colours until its photo is ready' : '');
  $('st-lbPrev').hidden = $('st-lbNext').hidden = list.length < 2;
  lb.classList.toggle('st-is-shiny', shiny);
  box.style.setProperty('--c', shiny ? 'var(--gold)' : st.c);
}
lbImg.onload = lbImg.onerror = () => lbImg.classList.remove('st-is-loading');
fan.onclick = e => {
  const card = e.target.closest('.st-card');
  if (!card || card.disabled) return;
  const g = GENS[state.gen];
  opener = card;
  list = g.starters.filter(s => s.file);
  // mid-deal a card can still hold the last generation's starter, which may have no photo in this one
  const i = list.indexOf(g.starters[+card.dataset.t]);
  if (i < 0) return;
  show(i);
  lb.showModal();
  lockScroll(lb);
  // grow out of the card that was clicked
  const c = card.getBoundingClientRect(), b = box.getBoundingClientRect();
  box.style.transformOrigin = `${c.left + c.width / 2 - b.left}px ${c.top + c.height / 2 - b.top}px`;
  replay(box, 'st-is-in');
};
$('st-lbPrev').onclick = () => show(at - 1);
$('st-lbNext').onclick = () => show(at + 1);
$('st-lbClose').onclick = () => lb.close();
lb.onclick = e => { if (e.target === lb) lb.close(); }; // a click on the backdrop lands on the dialog itself
// Escape belongs to this dialog: a docked cart drawer listens on document and would close instead
lb.onkeydown = e => { if (e.key === 'Escape') e.stopPropagation(); if (e.key === 'ArrowLeft') show(at - 1); if (e.key === 'ArrowRight') show(at + 1); };
lb.onclose = () => { unlockScroll(lb); opener?.focus(); };

// loops pause while the gallery is off screen
new IntersectionObserver(([e]) => stage.classList.toggle('st-is-paused', !e.isIntersecting)).observe(stage);
// Pause motion holds the Shiny shine loops (the viewer's too) until pressed again; the label carries the state
const motion = $('st-motion');
motion.onclick = () => {
  motion.style.minWidth = motion.getBoundingClientRect().width + 'px'; // keeps the longer "Pause motion" width, so the chip doesn't jump
  motion.textContent = root.classList.toggle('st-is-still') ? 'Play motion' : 'Pause motion';
};
cards.forEach(fillCard);
render();
centre();
root.classList.add('st-ready'); // the section's fallback script checks this on load
